import * as React from 'react';
import {
  Gauge,
  Settings2,
  ArrowUpCircle,
  ArrowDownCircle,
  Target,
  Activity,
  Zap,
  Cpu,
  RefreshCcw,
  AlertTriangle,
  MoveHorizontal,
  CheckCircle2,
  Terminal,
  ChevronDown,
  ChevronUp,
  BookOpen,
  Loader2,
  Wind,
  Home,
  BarChart3
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Toaster, toast } from '@/components/ui/sonner';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { SETUP_MATRIX, CONDITIONS, type Condition, type Adjustment } from '@shared/setup-matrix-data';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
const POSITIVE_ADJUSTMENTS: Adjustment[] = ['Increase', 'Stiffer', 'Stiffen', 'Higher', 'More', 'More Positive', 'Less Negative', 'Increase Static Cross Weight', 'Move Nose Weight Forward', 'Raise Rear ARB'];
const NEGATIVE_ADJUSTMENTS: Adjustment[] = ['Decrease', 'Softer', 'Soften', 'Lower', 'Less', 'More Negative', 'Less Positive', 'Reduce LF Camber Angle', 'Lower Rear ARB Diameter'];
const NEUTRAL_ADJUSTMENTS: Adjustment[] = ['Pos / Neg'];
const LOOSE_CONDITIONS: Condition[] = ['Loose on Entry', 'Loose in Center', 'Loose on Exit', 'Loose Overall'];
const TIGHT_CONDITIONS: Condition[] = ['Tight on Entry', 'Tight in Center', 'Tight on Exit', 'Tight Overall'];
const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  'Front': <Target className="w-4 h-4 mr-2 text-hpmBlue" />,
  'Left Front': <Activity className="w-4 h-4 mr-2 text-hpmBlue" />,
  'Right Front': <Activity className="w-4 h-4 mr-2 text-hpmBlue" />,
  'Left Rear': <Zap className="w-4 h-4 mr-2 text-hpmBlue" />,
  'Right Rear': <Zap className="w-4 h-4 mr-2 text-hpmBlue" />,
  'Rear': <Cpu className="w-4 h-4 mr-2 text-hpmBlue" />,
};
const StrategyColumn: React.FC<{
  title: string;
  items: string[];
  accentColor?: 'blue' | 'orange'
}> = ({ title, items, accentColor = 'blue' }) => {
  const isBlue = accentColor === 'blue';
  const badgeClasses = isBlue
    ? "bg-hpmBlue/20 text-hpmBlue border-hpmBlue/40"
    : "bg-orange-500/20 text-orange-400 border-orange-500/40";
  const bulletClasses = isBlue ? "text-hpmBlue" : "text-orange-400";
  const phaseClasses = isBlue ? "text-hpmBlue/70 border-hpmBlue/30" : "text-orange-400/70 border-orange-500/30";
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <Badge className={cn("text-[10px] font-black uppercase italic tracking-tighter", badgeClasses)}>
          {title} Phase
        </Badge>
      </div>
      <ul className="space-y-3 text-[11px] font-bold uppercase tracking-tight text-foreground/90 leading-tight">
        {items.map((text, i) => {
          if (text.includes(':')) {
            const [label, detail] = text.split(':');
            return (
              <li key={i} className="flex flex-col gap-1">
                <span className={cn("text-[9px] italic font-black uppercase opacity-60", phaseClasses)}>{label.trim()}</span>
                <span className={cn("pl-3 border-l-2 border-hpmBlue/20", isBlue ? "text-hpmBlue/90" : "text-orange-400/90")}>{detail.trim()}</span>
              </li>
            );
          }
          return (
            <li key={i} className="flex gap-2 items-start">
              <span className={cn("font-black mt-0.5", bulletClasses)}>»</span>
              <span className="flex-1">{text}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
export const AdjustmentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedCondition, setSelectedCondition] = React.useState<Condition | ''>('');
  const [isBriefingOpen, setIsBriefingOpen] = React.useState(true);
  const [isProcessing, setIsProcessing] = React.useState(false);
  React.useEffect(() => {
    if (selectedCondition) {
      setIsBriefingOpen(true);
      setIsProcessing(true);
      const timer = setTimeout(() => setIsProcessing(false), 800);
      return () => clearTimeout(timer);
    }
  }, [selectedCondition]);
  const filteredMatrix = React.useMemo(() => {
    if (!selectedCondition) return [];
    return SETUP_MATRIX.map(category => ({
      ...category,
      items: category.items.filter(item =>
        item.adjustments[selectedCondition as Condition] !== null
      )
    })).filter(category => category.items.length > 0);
  }, [selectedCondition]);
  const isLooseCondition = React.useMemo(() =>
    selectedCondition !== '' && LOOSE_CONDITIONS.includes(selectedCondition as Condition),
  [selectedCondition]);
  const isTightCondition = React.useMemo(() =>
    selectedCondition !== '' && TIGHT_CONDITIONS.includes(selectedCondition as Condition),
  [selectedCondition]);
  const handleReset = React.useCallback(() => {
    setSelectedCondition('');
    toast.info('Terminal Signal: Flush', {
      description: 'Matrix cleared. Telemetry buffers reset to baseline.',
      duration: 3000,
    });
  }, []);
  const getBadgeColor = (adjustment: Adjustment) => {
    if (!adjustment) return "bg-muted/20 text-muted-foreground border-transparent";
    if (POSITIVE_ADJUSTMENTS.includes(adjustment)) {
      return "bg-emerald-500/10 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20 shadow-[0_0_12px_rgba(52,211,153,0.15)]";
    }
    if (NEGATIVE_ADJUSTMENTS.includes(adjustment)) {
      return "bg-orange-500/10 text-orange-400 border-orange-500/30 hover:bg-orange-500/20 shadow-[0_0_12px_rgba(251,146,60,0.15)]";
    }
    if (NEUTRAL_ADJUSTMENTS.includes(adjustment)) {
      return "bg-hpmBlue/20 text-hpmBlue border-hpmBlue/50 hover:bg-hpmBlue/30 shadow-[0_0_20px_rgba(0,136,204,0.3)]";
    }
    return "bg-muted/20 text-muted-foreground border-transparent";
  };
  const getAdjustmentIcon = (adjustment: Adjustment) => {
    if (!adjustment) return null;
    if (POSITIVE_ADJUSTMENTS.includes(adjustment)) {
      return <ArrowUpCircle className="w-3.5 h-3.5 ml-1.5 opacity-70" />;
    }
    if (NEGATIVE_ADJUSTMENTS.includes(adjustment)) {
      return <ArrowDownCircle className="w-3.5 h-3.5 ml-1.5 opacity-70" />;
    }
    if (NEUTRAL_ADJUSTMENTS.includes(adjustment)) {
      return <MoveHorizontal className="w-3.5 h-3.5 ml-1.5 opacity-90" />;
    }
    return null;
  };
  return (
    <TooltipProvider delayDuration={200}>
      <div className="relative min-h-screen bg-hpmDarker overflow-x-hidden font-sans selection:bg-hpmBlue/40">
        <Toaster theme="dark" position="bottom-right" closeButton />
        {/* Navigation Bar */}
        <div className="absolute top-6 left-6 z-[60]">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/')}
            className="bg-hpmDark/40 backdrop-blur-md border-hpmBlue/20 text-hpmBlue/60 hover:text-hpmBlue hover:border-hpmBlue/60 hover:bg-hpmBlue/10 font-black italic uppercase tracking-widest gap-2 transition-all duration-300"
          >
            <Home className="w-4 h-4" />
            RTB // BASE
          </Button>
        </div>
        {/* Scanline Effect Overlay */}
        <div
          className="fixed inset-0 pointer-events-none z-50 opacity-[0.03]"
          style={{
            background: 'linear-gradient(rgba(18, 16, 16, 0) 50%, rgba(0, 0, 0, 0.25) 50%), linear-gradient(90deg, rgba(255, 0, 0, 0.04), rgba(0, 255, 0, 0.01), rgba(0, 0, 255, 0.04))',
            backgroundSize: '100% 4px, 3px 100%'
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <header className="mb-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-hpmBlue/30 bg-hpmBlue/5 backdrop-blur-md mb-6"
            >
              <div className={cn("w-2 h-2 rounded-full shadow-[0_0_8px_#0088CC]", isProcessing ? "bg-hpmBlue animate-ping" : "bg-hpmBlue animate-pulse")} />
              <span className="text-[9px] font-black uppercase tracking-[0.2em] text-hpmBlue/90 italic">
                Engineering Terminal v4.2.0 // Uplink {isProcessing ? 'Processing' : 'Stable'}
              </span>
            </motion.div>
            <h1 className="text-5xl sm:text-7xl md:text-9xl font-black italic tracking-tighter uppercase mb-4 select-none leading-none">
              HPM <span className="text-hpmBlue drop-shadow-[0_0_25px_rgba(0,136,204,0.4)]">TERMINAL</span>
            </h1>
            <p className="text-muted-foreground/40 text-[10px] sm:text-xs font-bold uppercase tracking-[0.6em] italic">
              Precison Setup Optimization Matrix
            </p>
          </header>
          <section className="max-w-2xl mx-auto mb-10 flex flex-col sm:row gap-4">
            <div className="flex-grow">
              <Select value={selectedCondition} onValueChange={(val) => setSelectedCondition(val as Condition)}>
                <SelectTrigger className="h-16 bg-hpmDark/60 border-hpmBlue/20 hover:border-hpmBlue/50 text-base sm:text-lg font-black uppercase italic tracking-wider rounded-xl transition-all shadow-2xl focus:ring-hpmBlue/50">
                  <SelectValue placeholder="SYMPTOM ANALYSIS..." />
                </SelectTrigger>
                <SelectContent className="bg-hpmDark border-hpmBlue/30 max-h-[400px]">
                  {CONDITIONS.map((cond) => (
                    <SelectItem key={cond} value={cond} className="font-bold uppercase italic py-3 focus:bg-hpmBlue focus:text-white cursor-pointer transition-colors">
                      {cond}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedCondition && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                whileHover={{ scale: 1.05 }}
                className="w-full sm:w-16"
              >
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleReset}
                  className="h-16 w-full border-red-500/20 text-red-400 hover:bg-red-500/10 hover:border-red-500/50 bg-hpmDark/60 rounded-xl transition-all shadow-lg"
                >
                  <RefreshCcw className={cn("w-6 h-6", isProcessing && "animate-spin")} />
                </Button>
              </motion.div>
            )}
          </section>
          <AnimatePresence mode="wait">
            {isLooseCondition && (
              <motion.section
                key="loose-brief"
                initial={{ opacity: 0, y: -10, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                className="max-w-4xl mx-auto mb-12"
              >
                <Collapsible open={isBriefingOpen} onOpenChange={setIsBriefingOpen}>
                  <Card className="border-hpmBlue/40 bg-hpmBlue/5 backdrop-blur-xl shadow-[0_0_40px_rgba(0,136,204,0.1)] overflow-hidden">
                    <CollapsibleTrigger asChild>
                      <div className="flex items-center justify-between p-5 cursor-pointer hover:bg-hpmBlue/10 transition-colors border-b border-hpmBlue/20">
                        <div className="flex items-center gap-3">
                          <Terminal className="w-5 h-5 text-hpmBlue" />
                          <h2 className="text-xs font-black uppercase tracking-[0.2em] italic text-hpmBlue">
                            Strategy: Corrective Measures (Loose)
                          </h2>
                        </div>
                        {isBriefingOpen ? <ChevronUp className="w-4 h-4 text-hpmBlue" /> : <ChevronDown className="w-4 h-4 text-hpmBlue" />}
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <CardContent className="p-8 grid grid-cols-1 md:grid-cols-3 gap-12">
                        <StrategyColumn
                          title="ENTRY"
                          items={["Shift Nose Weight Forward", "Decrease LF Camber Gain", "Verify Brake Bias (%) Increase"]}
                        />
                        <StrategyColumn
                          title="MID-CORNER"
                          items={["Soften Rear Anti-Roll Bar", "Increase Static Cross-Weight", "Balance Spring Loading"]}
                        />
                        <StrategyColumn
                          title="EXIT DRIVE"
                          items={["Increase Spoiler Angle", "Lower Track Bar (R)", "Soften RR Compression"]}
                        />
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              </motion.section>
            )}
            {isTightCondition && (
              <motion.section
                key="tight-brief"
                initial={{ opacity: 0, y: -10, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                className="max-w-4xl mx-auto mb-12"
              >
                <Collapsible open={isBriefingOpen} onOpenChange={setIsBriefingOpen}>
                  <Card className="border-orange-500/40 bg-orange-500/5 backdrop-blur-xl shadow-[0_0_40px_rgba(249,115,22,0.1)] overflow-hidden">
                    <CollapsibleTrigger asChild>
                      <div className="flex items-center justify-between p-5 cursor-pointer hover:bg-orange-500/10 transition-colors border-b border-orange-500/20">
                        <div className="flex items-center gap-3">
                          <Wind className="w-5 h-5 text-orange-400" />
                          <h2 className="text-xs font-black uppercase tracking-[0.2em] italic text-orange-400">
                            Strategy: Corrective Measures (Tight)
                          </h2>
                        </div>
                        {isBriefingOpen ? <ChevronUp className="w-4 h-4 text-orange-400" /> : <ChevronDown className="w-4 h-4 text-orange-400" />}
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <CardContent className="p-8 grid grid-cols-1 md:grid-cols-3 gap-12">
                        <StrategyColumn
                          title="ENTRY"
                          accentColor="orange"
                          items={["Shift Nose Weight Rearward", "Increase LF Camber Angle", "Decrease Brake Bias (%)"]}
                        />
                        <StrategyColumn
                          title="MID-CORNER"
                          accentColor="orange"
                          items={["Stiffen Rear Anti-Roll Bar", "Decrease Static Cross-Weight", "Adjust LR Shock Rebound"]}
                        />
                        <StrategyColumn
                          title="EXIT DRIVE"
                          accentColor="orange"
                          items={["Decrease Spoiler Angle", "Raise Track Bar (R)", "Stiffen RR Compression"]}
                        />
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              </motion.section>
            )}
          </AnimatePresence>
          <main className="relative min-h-[500px]">
            <AnimatePresence mode="wait">
              {selectedCondition ? (
                filteredMatrix.length > 0 ? (
                  <motion.div
                    key="results"
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -15 }}
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                  >
                    {filteredMatrix.map((cat, idx) => (
                      <motion.div
                        key={cat.category}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.05 }}
                      >
                        <Card className="border-white/5 bg-hpmDark/40 backdrop-blur-xl hover:border-hpmBlue/40 transition-all duration-500 group overflow-hidden h-full flex flex-col shadow-2xl">
                          <CardHeader className="bg-hpmBlue/5 py-4 border-b border-white/5">
                            <CardTitle className="text-[11px] font-black uppercase tracking-[0.3em] text-hpmBlue flex items-center italic">
                              {CATEGORY_ICONS[cat.category] || <Settings2 className="w-4 h-4 mr-2" />}
                              {cat.category}
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-0 flex-grow">
                            <ScrollArea className="min-h-[220px] max-h-[420px] px-6 py-5">
                              <ul className="space-y-6">
                                {cat.items.map((item) => {
                                  const adj = item.adjustments[selectedCondition as Condition];
                                  return (
                                    <li key={item.name} className="flex items-center justify-between group/item">
                                      <span className="text-[10px] font-black text-foreground/40 group-hover/item:text-foreground/90 transition-colors uppercase tracking-tight pr-4">
                                        {item.name}
                                      </span>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span className="inline-block cursor-help flex-shrink-0">
                                            <Badge
                                              variant="outline"
                                              className={cn(
                                                "px-3 py-1.5 text-[9px] font-black uppercase tracking-widest border transition-all duration-300 min-w-[100px] justify-center",
                                                getBadgeColor(adj)
                                              )}
                                            >
                                              {adj}
                                              {getAdjustmentIcon(adj)}
                                            </Badge>
                                          </span>
                                        </TooltipTrigger>
                                        <TooltipContent className="bg-hpmDark border-hpmBlue/40 text-[9px] font-black uppercase tracking-[0.2em] text-hpmBlue italic p-3">
                                          ENGINEERING PRIORITY: SYSTEM DELTA ADJUSTMENT
                                        </TooltipContent>
                                      </Tooltip>
                                    </li>
                                  );
                                })}
                              </ul>
                            </ScrollArea>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </motion.div>
                ) : (
                  <motion.div
                    key="empty"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="flex flex-col items-center justify-center py-32 text-center"
                  >
                    <div className="relative mb-8 w-32 h-32 flex items-center justify-center">
                      <motion.div
                        animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.3, 0.1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="absolute inset-0 bg-yellow-500 rounded-full blur-3xl"
                      />
                      <AlertTriangle className="w-16 h-16 text-yellow-500/40 relative z-10" />
                    </div>
                    <h3 className="text-3xl font-black uppercase italic text-foreground/80 mb-4 tracking-tighter">DATA INCONCLUSIVE</h3>
                    <p className="text-muted-foreground/30 text-[10px] max-w-sm uppercase font-black tracking-[0.3em] leading-relaxed italic">
                      Telemetry scan indicates balanced profile. No optimization deltas detected within current sector parameters.
                    </p>
                  </motion.div>
                )
              ) : (
                <motion.div
                  key="idle"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col items-center justify-center py-24 text-center space-y-12"
                >
                  <div className="relative">
                    <div className="absolute inset-0 bg-hpmBlue/20 blur-[100px] animate-pulse rounded-full" />
                    <div className="relative w-64 h-64 rounded-full border border-hpmBlue/10 flex items-center justify-center overflow-hidden">
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                        className="absolute inset-0 border-2 border-dashed border-hpmBlue/5 rounded-full scale-110"
                      />
                      <motion.div
                        animate={{ rotate: -360 }}
                        transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                        className="absolute inset-12 border-t-2 border-b-2 border-hpmBlue/30 rounded-full"
                      />
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
                        <Gauge className="w-20 h-20 text-hpmBlue/20 mb-2" />
                        <span className="text-[8px] font-black text-hpmBlue/40 uppercase tracking-[0.5em]">STANDBY</span>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-4xl font-black text-white/5 uppercase italic tracking-[0.4em]">SYSTEM_READY</h3>
                    <p className="text-muted-foreground/10 text-[9px] font-black uppercase tracking-[0.8em]">
                      AWAITING PILOT INPUT • RADAR SWEEP NOMINAL
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </main>
          <footer className="mt-28 pb-12 border-t border-white/5 flex flex-col md:row items-center justify-between gap-10">
            <div className="flex items-center gap-4 opacity-40 hover:opacity-100 transition-all duration-700">
              <BarChart3 className="w-5 h-5 text-hpmBlue" />
              <div className="flex flex-col">
                <span className="text-[10px] font-black uppercase tracking-[0.4em] italic text-white">HPM ENGINEERING</span>
                <span className="text-[8px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Core Strategy v4.2.0</span>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-8">
              <div className="flex items-center gap-2">
                <div className={cn("w-1.5 h-1.5 rounded-full bg-hpmBlue", selectedCondition ? "animate-ping" : "opacity-30")} />
                <span className="text-[9px] font-black uppercase tracking-[0.2em] text-muted-foreground/50 italic">System Load: {selectedCondition ? 'High' : 'Nominal'}</span>
              </div>
              <Badge variant="outline" className={cn(
                "px-5 py-2 text-[10px] font-black uppercase tracking-widest border transition-all duration-700",
                selectedCondition
                  ? "border-hpmBlue/60 text-hpmBlue bg-hpmBlue/10 shadow-[0_0_20px_rgba(0,136,204,0.3)]"
                  : "border-emerald-500/20 text-emerald-500/50 bg-emerald-500/5"
              )}>
                {selectedCondition ? "UPLINK_ENGAGED" : "BASELINE_STABLE"}
                <CheckCircle2 className="w-4 h-4 ml-3" />
              </Badge>
            </div>
          </footer>
        </div>
      </div>
    </TooltipProvider>
  );
};
export default AdjustmentsPage;